import React from 'react';
import { FaExclamationTriangle, FaBan } from 'react-icons/fa';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { ItemStockInfo } from '../../hooks/useStockStatus';
import './StockIndicator.scss';

interface StockIndicatorProps {
  hasShortage: boolean;
  hasWarning: boolean;
  shortageInfo?: ItemStockInfo;
  warningInfo?: ItemStockInfo;
  showTooltip?: boolean;
  size?: 'small' | 'medium' | 'large';
}

/**
 * StockIndicator Component
 * 
 * Displays visual indicators for item stock status:
 * - Red ban icon for shortages (out of stock or missing ingredients)
 * - Yellow warning triangle for low stock warnings
 * - Optional tooltip with detailed information
 */
const StockIndicator: React.FC<StockIndicatorProps> = ({
  hasShortage,
  hasWarning,
  shortageInfo,
  warningInfo,
  showTooltip = true,
  size = 'small',
}) => {
  // Priority: Show shortage over warning
  if (!hasShortage && !hasWarning) {
    return null;
  }

  const isShortage = hasShortage;
  const info = isShortage ? shortageInfo : warningInfo;

  // Build tooltip message
  const buildTooltipMessage = (): string => {
    if (!info) return '';

    if (isShortage) {
      if (info.missing_ingredients === null) {
        // Twin ingredient system shortage
        return `${info.item_name}: Sin stock disponible`;
      } else if (info.missing_ingredients && info.missing_ingredients.length > 0) {
        return `${info.item_name}: Faltan ingredientes - ${info.missing_ingredients.join(', ')}`;
      } else {
        return `${info.item_name}: Sin stock`;
      }
    } else {
      // Warning
      if (info.low_stock_ingredients && info.low_stock_ingredients.length > 0) {
        return `${info.item_name}: Stock bajo - ${info.low_stock_ingredients.join(', ')}`;
      } else {
        return `${info.item_name}: Stock bajo`;
      }
    }
  };

  const Icon = isShortage ? FaBan : FaExclamationTriangle;
  const className = `stock-indicator ${isShortage ? 'shortage' : 'warning'} stock-indicator--${size}`;
  const tooltipMessage = buildTooltipMessage();

  const indicator = (
    <span className={className} aria-label={tooltipMessage}>
      <Icon />
    </span>
  );

  if (showTooltip && tooltipMessage) {
    return (
      <OverlayTrigger
        placement="top"
        overlay={
          <Tooltip id={`stock-tooltip-${info?.item_name}`}>
            {tooltipMessage}
          </Tooltip>
        }
      >
        {indicator}
      </OverlayTrigger>
    );
  }

  return indicator;
};

export default StockIndicator;
